import { type ReactElement, type ReactNode, type RefObject, useEffect, useRef } from "react"

import { draggable, dropTargetForElements, monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter"
import type { BaseEventPayload, CleanupFn, ElementDragType } from "@atlaskit/pragmatic-drag-and-drop/types"

type Props = {
    onDrop(params: BaseEventPayload<ElementDragType>): void
    children: ReactNode
}

const DragDropWrapper = ({ onDrop, children }: Props): ReactElement => {
    const listRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)

    useEffect((): CleanupFn => {
        if (!listRef.current) return

        return dropTargetForElements({
            element: listRef.current
        })
    }, [])

    useEffect((): CleanupFn => {
        return monitorForElements({ onDrop })
    }, [onDrop])

    return <div className="flex flex-col">{children}</div>
}

export function useDraggableItemRefs(params: {
    index: number
    id: string | number
    dropType: string
    payload?: object
}): {
    containerRef: RefObject<HTMLDivElement>
    grabberRef: RefObject<HTMLSpanElement>
} {
    const containerRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)
    const grabberRef: RefObject<HTMLSpanElement> = useRef<HTMLSpanElement>(null)

    useEffect((): CleanupFn => {
        if (!containerRef.current || !grabberRef.current) return

        return draggable({
            element: containerRef.current,
            dragHandle: grabberRef.current,
            getInitialData: () => ({ index: params.index, uuid: params.id, payload: params.payload }) // source
        })
    }, [params.id, params.index, params.payload])

    useEffect((): CleanupFn => {
        if (!containerRef.current) return

        return dropTargetForElements({
            element: containerRef.current,
            getData: () => ({ index: params.index, type: params.dropType }), // location
            onDragEnter(): void {
                containerRef.current.classList.add("dnd-dragging")
            },
            onDragLeave(): void {
                containerRef.current.classList.remove("dnd-dragging")
            },
            onDrop(): void {
                containerRef.current.classList.remove("dnd-dragging")
            }
        })
    }, [params.index, params.dropType])

    return { containerRef, grabberRef }
}

export default DragDropWrapper
